import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { useAuthStore } from '@/app/store/authStore';
import type { ApiError } from '@/types/api.types';

// The single Axios instance used app-wide. Every service module imports
// this rather than creating its own client, so auth headers, retries,
// and error normalization are handled in exactly one place.
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-Authorization'] = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Single-flight refresh: concurrent 401s share one refresh call
    // instead of each firing their own /auth/refresh request.
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== endpoints.auth.logout) {
      originalRequest._retry = true;
    try {
        refreshPromise ??= useAuthStore.getState().refreshAccessToken();
        const newToken = await refreshPromise;
        refreshPromise = null;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest.headers['X-Authorization'] = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    }

    // Normalize every failure into ApiError shape before it reaches
    // React Query's `error` — callers never branch on Axios internals.
    const normalized: ApiError = error.response?.data ?? {
      code: 'NETWORK_ERROR',
      message: error.message,
    };
    return Promise.reject(normalized);
  },
);
